'use client'

/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { UseQueryResult } from '@tanstack/react-query'
import { useCallback, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'

import type { BookCollectionDetails } from '@package/api'
import type { CarouselApi } from '@ui/web/components'
import type { RenderLink } from '@ui/web/utils'
import { Autoplay, Button, Carousel, CarouselContent, CarouselItem, Skeleton } from '@ui/web/components'
import { BookRowLeftChevronIcon, BookRowRightChevronIcon } from '@ui/web/icon'
import { useMediaQuery } from '@helper/web/hooks'
import cn from '@config/tailwind/cn'

interface BaseBookCollectionRowProps {
  className?: string
  extraKey?: string
  renderLink: RenderLink
}

interface BookCollectionRowWithQueryProps extends BaseBookCollectionRowProps {
  /**
   * Query result that resolves to a list of book collections.
   */
  query: UseQueryResult<BookCollectionDetails[], Error>
  data?: never
}

interface BookCollectionRowWithDataProps extends BaseBookCollectionRowProps {
  query?: never
  /**
   * Direct array of book collection details to display.
   */
  data: BookCollectionDetails[]
}

type BookCollectionRowProps = BookCollectionRowWithQueryProps | BookCollectionRowWithDataProps

export function BookCollectionRow({ className, extraKey, renderLink, query, data }: BookCollectionRowProps) {
  const [api, setApi] = useState<CarouselApi>()

  const is2Xl = useMediaQuery('(min-width: 1536px)', { defaultValue: false })

  const plugin = useRef(Autoplay({ delay: 4000 + Math.floor(Math.random() * 6001), stopOnInteraction: true }))

  const scrollPrev = useCallback(() => {
    if (api) {
      api.scrollPrev()
    }
  }, [api])

  const scrollNext = useCallback(() => {
    if (api) {
      api.scrollNext()
    }
  }, [api])

  const hasQuery = Boolean(query)
  const hasData = Boolean(data)

  const queryData = hasQuery ? query!.data : undefined
  const isQueryPending = hasQuery ? query!.isPending : false
  const isQueryError = hasQuery ? query!.isError : false
  const isQueryRefetching = hasQuery ? query!.isRefetching : false

  const baseData = hasData ? data! : (queryData ?? [])

  const carouselData: BookCollectionDetails[] = (() => {
    if (baseData.length === 0) return []

    if (is2Xl && baseData.length < 6) {
      return [...baseData, ...baseData]
    }

    return baseData
  })()

  if (hasQuery && isQueryPending) {
    return <BookCollectionRowLoading className={className} />
  }

  if (hasQuery && isQueryError) {
    return (
      <BookCollectionRowError
        className={className}
        onRetry={() => void query?.refetch()}
        isRefetching={isQueryRefetching}
      />
    )
  }

  if (carouselData.length === 0) {
    return null
  }

  return (
    <div className={cn('srd:w-full srd:mx-auto srd:relative', className)}>
      <Carousel
        plugins={[plugin.current]}
        setApi={setApi}
        className='srd:w-full'
        opts={{
          align: 'center',
          loop: true,
          slidesToScroll: 1,
        }}
      >
        <CarouselContent className='srd:-ml-2'>
          {carouselData.map((collection) => (
            <CarouselItem
              key={`book-collection-${collection.id}-${extraKey}`}
              className='srd:pl-2 srd:basis-4/5 srd:sm:basis-3/5 srd:md:basis-1/2 srd:lg:basis-2/5 srd:xl:basis-1/3 srd:2xl:basis-1/4'
            >
              {renderLink({
                children: (
                  <motion.div
                    initial={{ opacity: 0, x: 10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className='srd:w-full srd:h-fit srd:relative'
                  >
                    <img
                      src={collection.coverUrl}
                      alt={collection.name}
                      className='srd:w-full srd:object-cover srd:rounded-md srd:aspect-5/4'
                      draggable={false}
                    />
                  </motion.div>
                ),
                meta: { bookCollectionId: collection.id, bookCollectionSlug: collection.slug },
              })}
            </CarouselItem>
          ))}
        </CarouselContent>
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className='srd:absolute srd:top-1/2 srd:left-8 srd:-translate-y-1/2 srd:-translate-x-1/2 srd:w-fit srd:h-fit srd:rounded-full srd:border-none srd:hover:bg-none srd:hover:bg-transparent'
            onClick={scrollPrev}
          >
            <BookRowLeftChevronIcon />
            <span className='srd:sr-only'>Koleksi bacaan sebelumnya</span>
          </motion.div>
        </AnimatePresence>
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className='srd:absolute srd:top-1/2 srd:right-8 srd:-translate-y-1/2 srd:translate-x-1/2 srd:w-fit srd:h-fit srd:rounded-full srd:border-none srd:hover:bg-none srd:hover:bg-transparent'
            onClick={scrollNext}
          >
            <BookRowRightChevronIcon />
            <span className='srd:sr-only'>Koleksi bacaan selanjutnya</span>
          </motion.div>
        </AnimatePresence>
      </Carousel>
    </div>
  )
}

interface BookCollectionRowErrorProps {
  className?: string
  onRetry: () => void
  isRefetching: boolean
}

export function BookCollectionRowError({ className, onRetry, isRefetching }: BookCollectionRowErrorProps) {
  return (
    <div
      className={cn(
        'srd:w-full srd:rounded-none srd:bg-neutrals-n-20 srd:px-4 srd:py-6 srd:flex srd:flex-col srd:items-center srd:justify-center srd:gap-y-3 srd:mx-auto',
        className,
      )}
    >
      <p className='srd:text-base srd:font-semibold srd:text-neutrals-black'>Oops, terjadi kesalahan</p>
      <p className='srd:text-sm srd:text-neutrals-black'>Gagal memuat daftar koleksi bacaan. Silakan coba lagi.</p>
      <Button type='button' onClick={onRetry} disabled={isRefetching} className='srd:px-6 srd:py-2 srd:rounded-full'>
        {isRefetching ? 'Memuat...' : 'Coba Lagi'}
      </Button>
    </div>
  )
}

interface BookCollectionRowLoadingProps {
  className?: string
}

export function BookCollectionRowLoading({ className }: BookCollectionRowLoadingProps) {
  const [api, setApi] = useState<CarouselApi>()

  const is2Xl = useMediaQuery('(min-width: 1536px)', { defaultValue: false })

  const plugin = useRef(Autoplay({ delay: 4000 + Math.floor(Math.random() * 6001), stopOnInteraction: true }))

  const scrollPrev = useCallback(() => {
    if (api) {
      api.scrollPrev()
    }
  }, [api])

  const scrollNext = useCallback(() => {
    if (api) {
      api.scrollNext()
    }
  }, [api])

  const carouselData = is2Xl ? new Array(8).fill(0) : new Array(6).fill(0)

  return (
    <div className={cn('srd:w-full srd:mx-auto srd:relative', className)}>
      <Carousel
        plugins={[plugin.current]}
        setApi={setApi}
        className='srd:w-full'
        opts={{
          align: 'center',
          loop: true,
          slidesToScroll: 1,
        }}
      >
        <CarouselContent className='srd:-ml-2'>
          {carouselData.map((_, index) => (
            <CarouselItem
              key={`book-collection-loading-${index}`}
              className='srd:pl-2 srd:basis-[80%] srd:sm:basis-[50%] srd:md:basis-2/3 srd:lg:basis-[40%] srd:xl:basis-[40%] srd:2xl:basis-1/4'
            >
              <motion.div
                initial={{ opacity: 0, x: 10 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className='srd:w-full srd:h-full srd:relative'
              >
                <Skeleton className='srd:w-full srd:h-full srd:aspect-5/4 srd:rounded-md' />
              </motion.div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className='srd:absolute srd:top-1/2 srd:left-8 srd:-translate-y-1/2 srd:-translate-x-1/2 srd:w-fit srd:h-fit srd:rounded-full srd:border-none srd:hover:bg-none srd:hover:bg-transparent'
            onClick={scrollPrev}
          >
            <BookRowLeftChevronIcon />
            <span className='srd:sr-only'>Koleksi bacaan sebelumnya</span>
          </motion.div>
        </AnimatePresence>
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Button
              variant='outline'
              size='icon'
              className='srd:absolute srd:top-1/2 srd:right-8 srd:-translate-y-1/2 srd:translate-x-1/2 srd:w-fit srd:h-fit srd:rounded-full srd:border-none srd:hover:bg-none srd:hover:bg-transparent'
              onClick={scrollNext}
            >
              <BookRowRightChevronIcon />
              <span className='srd:sr-only'>Koleksi bacaan selanjutnya</span>
            </Button>
          </motion.div>
        </AnimatePresence>
      </Carousel>
    </div>
  )
}
